import type {
  EmbryoGenomicResult,
  EmbryoReport,
  EmbryoReportCreateInput,
  EmbryoReportUpdateInput,
} from "@src/packages/cycles/hooks/useEmbryoReport";

export type ReportTestType = EmbryoReport["testType"];
export type ReportState = EmbryoReport["reportState"];
export type EmbryoResult = EmbryoGenomicResult["result"];
export type EmbryoSex = EmbryoGenomicResult["sex"];
export type TransferRecommendation =
  EmbryoGenomicResult["transferRecommendation"];

export type EmbryoReportFormEmbryo = {
  sampleId: string;
  embryoNumber: string;
  grade: string;
  biopsyDay: string;
  biopsyDate: string;
  karyotype: string;
  sex: EmbryoSex | "";
  result: EmbryoResult | "";
  transferRecommendation: TransferRecommendation | "";
  comments: string;
};

export type EmbryoReportFormValues = {
  reportState: ReportState;
  reportDate: string;
  biopsyDate: string;
  retrievalDate: string;
  referringClinic: string;
  referringPhysician: string;
  laboratory: string;
  indication: string;
  testType: ReportTestType;
  sampleType: string;
  correctionReason: string;
  attachmentId: string | null;
  embryos: EmbryoReportFormEmbryo[];
};

export function toEmbryoReportFormValues(
  report: EmbryoReport,
): EmbryoReportFormValues {
  return {
    reportState: report.reportState,
    reportDate: report.reportDate,
    biopsyDate: report.biopsyDate,
    retrievalDate: report.retrievalDate,
    referringClinic: report.referringClinic,
    referringPhysician: report.referringPhysician,
    laboratory: report.laboratory,
    indication: report.indication,
    testType: report.testType,
    sampleType: report.sampleType,
    correctionReason: "",
    attachmentId: report.attachment?.uploadId ?? null,
    embryos: report.embryos.map((embryo) => ({
      sampleId: embryo.sampleId,
      embryoNumber: String(embryo.embryoNumber),
      grade: embryo.grade ?? "",
      biopsyDay: embryo.biopsyDay == null ? "" : String(embryo.biopsyDay),
      biopsyDate: embryo.biopsyDate ?? "",
      karyotype: embryo.karyotype,
      sex: embryo.sex,
      result: embryo.result,
      transferRecommendation: embryo.transferRecommendation,
      comments: embryo.comments ?? "",
    })),
  };
}

function isEmbryoSex(value: string): value is EmbryoSex {
  return value === "XX" || value === "XY";
}

function isEmbryoResult(value: string): value is EmbryoResult {
  return (
    value === "euploid" ||
    value === "aneuploid" ||
    value === "mosaic" ||
    value === "no_result"
  );
}

function isTransferRecommendation(
  value: string,
): value is TransferRecommendation {
  return value === "yes" || value === "no" || value === "with_counseling";
}

type EmbryoReportFields = Omit<EmbryoReportCreateInput, "embryos"> & {
  embryos: NonNullable<EmbryoReportCreateInput["embryos"]>;
};

function toEmbryoReportFields(
  values: EmbryoReportFormValues,
): EmbryoReportFields | null {
  if (
    values.reportDate === "" ||
    values.biopsyDate === "" ||
    values.retrievalDate === "" ||
    values.referringClinic.trim() === "" ||
    values.referringPhysician.trim() === "" ||
    values.laboratory.trim() === "" ||
    values.indication.trim() === "" ||
    values.sampleType.trim() === ""
  ) {
    return null;
  }

  const embryos: NonNullable<EmbryoReportCreateInput["embryos"]> = [];
  for (const embryo of values.embryos) {
    const embryoNumber = Number(embryo.embryoNumber);
    const biopsyDay = embryo.biopsyDay === "" ? null : Number(embryo.biopsyDay);
    if (
      embryo.sampleId.trim() === "" ||
      !Number.isInteger(embryoNumber) ||
      embryoNumber < 1 ||
      (biopsyDay !== null && !Number.isInteger(biopsyDay)) ||
      embryo.karyotype.trim() === "" ||
      !isEmbryoSex(embryo.sex) ||
      !isEmbryoResult(embryo.result) ||
      !isTransferRecommendation(embryo.transferRecommendation)
    ) {
      return null;
    }

    embryos.push({
      sampleId: embryo.sampleId.trim(),
      embryoNumber,
      grade: embryo.grade.trim() || null,
      biopsyDay,
      biopsyDate: embryo.biopsyDate || null,
      karyotype: embryo.karyotype.trim(),
      sex: embryo.sex,
      result: embryo.result,
      transferRecommendation: embryo.transferRecommendation,
      comments: embryo.comments.trim() || null,
    });
  }

  return {
    reportDate: values.reportDate,
    biopsyDate: values.biopsyDate,
    retrievalDate: values.retrievalDate,
    referringClinic: values.referringClinic.trim(),
    referringPhysician: values.referringPhysician.trim(),
    laboratory: values.laboratory.trim(),
    indication: values.indication.trim(),
    testType: values.testType,
    sampleType: values.sampleType.trim(),
    reportState: values.reportState,
    attachmentId: values.attachmentId,
    embryos,
  };
}

export function toEmbryoReportCreateInput(
  values: EmbryoReportFormValues,
): EmbryoReportCreateInput | null {
  return toEmbryoReportFields(values);
}

export function toEmbryoReportUpdateInput(
  values: EmbryoReportFormValues,
): EmbryoReportUpdateInput | null {
  const fields = toEmbryoReportFields(values);
  const correctionReason = values.correctionReason.trim();
  if (!fields || correctionReason === "") return null;

  const { reportState: _reportState, ...updateFields } = fields;
  return { ...updateFields, correctionReason };
}
